import { useEffect } from 'react';
import { InteractionManager, Linking, Platform } from 'react-native';

import { useAuth } from '../../contexts/AuthContext';
import { openProWelcome } from '../../core/navigation/proWelcomeController';
import { consumeBillingReturnPending } from '../../services/billingService';

function billingParamFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    if (url.includes('billing/success') || url.includes('billing=success')) {
      return 'success';
    }
    if (url.includes('billing/cancel') || url.includes('billing=cancel')) {
      return 'cancel';
    }
    if (url.includes('billing/portal-return') || url.includes('billing=portal')) {
      return 'portal';
    }
    const parsed = new URL(url);
    return parsed.searchParams.get('billing');
  } catch {
    return null;
  }
}

function showProWelcomeWhenSafe(): void {
  InteractionManager.runAfterInteractions(() => {
    setTimeout(() => openProWelcome(), 450);
  });
}

/**
 * Refresh profile after Stripe return.
 * Web: `/thanks` is the post-purchase page. `?billing=success` (Open dashboard
 * from that page) → Pro welcome.
 * Native: Pro welcome is opened from useBillingActions after openAuth resolves
 * (AppState-triggered modals here were freezing the UI after InAppBrowser).
 */
export function BillingReturnListener() {
  const { refreshUser, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;

    if (Platform.OS === 'web') {
      const g = globalThis as {
        location?: { search: string; href: string };
        history?: { replaceState: (data: unknown, unused: string, url?: string) => void };
      };
      if (!g.location) return;

      const param = new URLSearchParams(g.location.search).get('billing');
      if (param === 'success' || param === 'cancel' || param === 'portal') {
        consumeBillingReturnPending();
        void refreshUser()
          .catch(() => undefined)
          .finally(() => {
            if (param === 'success') showProWelcomeWhenSafe();
          });
        if (g.history?.replaceState) {
          const url = new URL(g.location.href);
          url.searchParams.delete('billing');
          g.history.replaceState({}, '', url.pathname + url.search + url.hash);
        }
      }
      return;
    }

    // Native: only refresh from deep link; do not present overlays here.
    const onUrl = ({ url }: { url: string }) => {
      const param = billingParamFromUrl(url);
      if (!param) return;
      void refreshUser().catch(() => undefined);
    };

    const sub = Linking.addEventListener('url', onUrl);
    return () => sub.remove();
  }, [isAuthenticated, refreshUser]);

  return null;
}
